<script setup>
import { computed, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import OverviewCards from './components/OverviewCards.vue';
import TaskKanban from './components/TaskKanban.vue';
import TaskModal from './components/TaskModal.vue';
import EventCalendar from './components/EventCalendar.vue';
import EventModal from './components/EventModal.vue';
import ScriptCard from './components/ScriptCard.vue';
import CountdownPanel from './components/CountdownPanel.vue';
import FieldKPIs from './components/FieldKPIs.vue';
import CityRanking from './components/CityRanking.vue';
import WorkerTable from './components/WorkerTable.vue';
import WorkerModal from './components/WorkerModal.vue';
import WorkerImportModal from './components/WorkerImportModal.vue';
import MediaKanban from './components/MediaKanban.vue';
import MediaModal from './components/MediaModal.vue';
import MapGenerator from './components/MapGenerator.vue';
import MapCanvas from './components/MapCanvas.vue';
import PinModal from './components/PinModal.vue';
import DeleteModal from 'dashboard/components/widgets/modal/DeleteModal.vue';
import FilterSelect from '../socialMonitoring/components/FilterSelect.vue';
import { CATEGORIES } from './utils/taskMeta';
import { EVENT_TYPES } from './utils/eventMeta';
import { WORKER_ROLES } from './utils/workerMeta';
import { ASSET_TYPES, PLATFORMS as MEDIA_PLATFORMS } from './utils/mediaMeta';
import { PIN_TYPES, pinTypeMeta } from './utils/pinMeta';

const store = useStore();

const VIEWS = [
  { value: 'overview', label: 'Visão Geral', icon: 'i-lucide-layout-dashboard' },
  { value: 'tasks', label: 'Tarefas', icon: 'i-lucide-list-checks' },
  { value: 'calendar', label: 'Calendário', icon: 'i-lucide-calendar' },
  { value: 'scripts', label: 'Scripts', icon: 'i-lucide-scroll-text' },
  { value: 'field', label: 'Campo', icon: 'i-lucide-map-pin' },
  { value: 'media', label: 'Mídia', icon: 'i-lucide-film' },
  { value: 'map', label: 'Mapa', icon: 'i-lucide-map' },
];
const activeView = ref('overview');

const campaign = computed(() => store.getters['electoralCampaign/getCampaign']);
const tasks = computed(() => store.getters['electoralCampaign/getTasks']);
const events = computed(() => store.getters['electoralCampaign/getEvents']);
const scripts = computed(() => store.getters['electoralCampaign/getScripts']);
const uiFlags = computed(() => store.getters['electoralCampaign/getUiFlags']);

const categoryFilter = ref('');
const taskModalOpen = ref(false);
const editingTask = ref(null);
const saving = ref(false);

const eventTypeFilter = ref('');
const eventModalOpen = ref(false);
const editingEvent = ref(null);
const eventDefaultDate = ref('');

const scriptTypeFilter = ref('');
const eventTypeOptions = computed(() => [
  { value: '', label: 'Todos os tipos' },
  ...EVENT_TYPES.map(t => ({ value: t.value, label: t.label, icon: t.icon })),
]);
const filteredScripts = computed(() =>
  scriptTypeFilter.value
    ? scripts.value.filter(s => s.eventType === scriptTypeFilter.value)
    : scripts.value
);

const workers = computed(() => store.getters['electoralCampaign/getWorkers']);
const fieldSummary = computed(() => store.getters['electoralCampaign/getFieldSummary']);
const fieldByCity = computed(() => store.getters['electoralCampaign/getFieldByCity']);

const workerRoleFilter = ref('');
const workerCityFilter = ref('');
const workerModalOpen = ref(false);
const editingWorker = ref(null);
const importModalOpen = ref(false);

const workerRoleOptions = [
  { value: '', label: 'Todos os papéis' },
  ...WORKER_ROLES.map(r => ({ value: r.value, label: r.label, dot: r.dot })),
];
const workerCityOptions = computed(() => {
  const cities = [...new Set(workers.value.map(w => w.city).filter(Boolean))].sort();
  return [{ value: '', label: 'Todas as cidades' }, ...cities.map(c => ({ value: c, label: c }))];
});
const filteredWorkers = computed(() => {
  let list = workers.value;
  if (workerRoleFilter.value) list = list.filter(w => w.role === workerRoleFilter.value);
  if (workerCityFilter.value) list = list.filter(w => w.city === workerCityFilter.value);
  return list;
});
const leaderWorkers = computed(() =>
  workers.value.filter(w => ['coordenador_municipal', 'lideranca_bairro'].includes(w.role))
);

const mediaAssets = computed(() => store.getters['electoralCampaign/getMediaAssets']);
const mediaTypeFilter = ref('');
const mediaPlatformFilter = ref('');
const mediaModalOpen = ref(false);
const editingAsset = ref(null);

const mediaTypeOptions = [
  { value: '', label: 'Todos os tipos' },
  ...ASSET_TYPES.map(t => ({ value: t.value, label: t.label, icon: t.icon })),
];
const mediaPlatformOptions = [
  { value: '', label: 'Todas as plataformas' },
  ...MEDIA_PLATFORMS.map(p => ({ value: p.value, label: p.label, icon: p.icon })),
];
const filteredAssets = computed(() => {
  let list = mediaAssets.value;
  if (mediaTypeFilter.value) list = list.filter(a => a.assetType === mediaTypeFilter.value);
  if (mediaPlatformFilter.value) {
    list = list.filter(a => a.platformTarget?.includes(mediaPlatformFilter.value));
  }
  return list;
});

// --- Map ---
const map = computed(() => store.getters['electoralCampaign/getMap']);
const pins = computed(() => store.getters['electoralCampaign/getPins']);
const activePinTypes = ref(['cabo', 'grupo', 'evento', 'area']);
const mapGenerating = ref(false);
const pinModalOpen = ref(false);
const editingPin = ref(null);
const pendingCoord = ref(null);
let mapPollTimer = null;

function togglePinType(v) {
  const idx = activePinTypes.value.indexOf(v);
  if (idx === -1) activePinTypes.value.push(v);
  else activePinTypes.value.splice(idx, 1);
}

async function loadMapAndPins() {
  await store.dispatch('electoralCampaign/fetchMap');
  if (map.value?.status === 'ready') {
    await store.dispatch('electoralCampaign/fetchPins', { includeWorkers: true });
  }
}

function stopMapPolling() {
  if (mapPollTimer) {
    clearInterval(mapPollTimer);
    mapPollTimer = null;
  }
}

function startMapPolling() {
  stopMapPolling();
  mapPollTimer = setInterval(async () => {
    const m = await store.dispatch('electoralCampaign/fetchMap');
    if (m?.status === 'ready' || m?.status === 'failed') {
      stopMapPolling();
      if (m.status === 'ready') {
        useAlert(`Mapa de ${m.city}/${m.state} pronto!`);
        await store.dispatch('electoralCampaign/fetchPins', { includeWorkers: true });
      } else {
        useAlert(`Falha: ${m.errorMessage || 'erro ao gerar'}`);
      }
    }
  }, 4000);
}

async function generateMap({ city, state }) {
  mapGenerating.value = true;
  try {
    await store.dispatch('electoralCampaign/generateMap', { city, state });
    startMapPolling();
    useAlert(`Gerando mapa de ${city}/${state}…`);
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Erro ao gerar mapa');
  } finally {
    mapGenerating.value = false;
  }
}

const deleteMapOpen = ref(false);
const deletingMap = ref(false);

function askDeleteMap() {
  deleteMapOpen.value = true;
}
async function confirmDeleteMap() {
  if (deletingMap.value) return;
  deletingMap.value = true;
  try {
    await store.dispatch('electoralCampaign/resetMap');
    await store.dispatch('electoralCampaign/fetchPins', { includeWorkers: false });
    useAlert('Mapa excluído. Agora você pode gerar outro.');
    deleteMapOpen.value = false;
  } catch (e) {
    useAlert(e?.response?.data?.error || e.message || 'Erro ao excluir');
  } finally {
    deletingMap.value = false;
  }
}

function onMapClick(coord) {
  pendingCoord.value = coord;
  editingPin.value = null;
  pinModalOpen.value = true;
}
function onPinClick(pin) {
  editingPin.value = pin;
  pendingCoord.value = null;
  pinModalOpen.value = true;
}
async function savePin(payload) {
  saving.value = true;
  try {
    if (payload.id) {
      await store.dispatch('electoralCampaign/updatePin', payload);
      useAlert('Pin atualizado');
    } else {
      await store.dispatch('electoralCampaign/createPin', payload);
      useAlert('Pin criado');
    }
    pinModalOpen.value = false;
    await store.dispatch('electoralCampaign/fetchPins', { includeWorkers: true });
  } catch (e) {
    useAlert(e?.response?.data?.errors?.join(', ') || 'Erro ao salvar pin');
  } finally {
    saving.value = false;
  }
}
async function deletePin(pin) {
  if (!window.confirm(`Excluir "${pin.title}"?`)) return;
  await store.dispatch('electoralCampaign/deletePin', pin.id);
  pinModalOpen.value = false;
  useAlert('Pin excluído');
}

const pinCounts = computed(() => {
  const counts = {};
  PIN_TYPES.forEach(t => {
    counts[t.value] = pins.value.filter(p => p.pinType === t.value).length;
  });
  return counts;
});

const categoryOptions = computed(() => [
  { value: '', label: 'Todas as categorias' },
  ...CATEGORIES.map(c => ({ value: c.value, label: c.label, dot: c.dot, icon: c.icon })),
]);

const filteredTasks = computed(() => {
  if (!categoryFilter.value) return tasks.value;
  return tasks.value.filter(t => t.category === categoryFilter.value);
});

async function loadTasks() {
  await store.dispatch('electoralCampaign/fetchTasks', {
    category: categoryFilter.value || undefined,
  });
}

watch(categoryFilter, loadTasks);

function openCreate() {
  editingTask.value = null;
  taskModalOpen.value = true;
}
function openEdit(task) {
  editingTask.value = task;
  taskModalOpen.value = true;
}
async function saveTask(payload) {
  saving.value = true;
  try {
    if (payload.id) {
      await store.dispatch('electoralCampaign/updateTask', payload);
      useAlert('Tarefa atualizada');
    } else {
      await store.dispatch('electoralCampaign/createTask', payload);
      useAlert('Tarefa criada');
    }
    taskModalOpen.value = false;
    await store.dispatch('electoralCampaign/fetchCampaign');
  } catch (e) {
    useAlert(e?.response?.data?.errors?.join(', ') || 'Erro ao salvar');
  } finally {
    saving.value = false;
  }
}
async function deleteTask(task) {
  if (!window.confirm(`Excluir "${task.title}"?`)) return;
  await store.dispatch('electoralCampaign/deleteTask', task.id);
  useAlert('Tarefa excluída');
  taskModalOpen.value = false;
  await store.dispatch('electoralCampaign/fetchCampaign');
}

function openNewEvent(iso = '') {
  editingEvent.value = null;
  eventDefaultDate.value = iso;
  eventModalOpen.value = true;
}
function openEditEvent(event) {
  editingEvent.value = event;
  eventDefaultDate.value = '';
  eventModalOpen.value = true;
}
async function saveEvent(payload) {
  saving.value = true;
  try {
    if (payload.id) {
      await store.dispatch('electoralCampaign/updateEvent', payload);
      useAlert('Evento atualizado');
    } else {
      await store.dispatch('electoralCampaign/createEvent', payload);
      useAlert('Evento criado');
    }
    eventModalOpen.value = false;
  } catch (e) {
    useAlert(e?.response?.data?.errors?.join(', ') || 'Erro ao salvar');
  } finally {
    saving.value = false;
  }
}
async function deleteEvent(event) {
  if (!window.confirm(`Excluir "${event.title}"?`)) return;
  await store.dispatch('electoralCampaign/deleteEvent', event.id);
  eventModalOpen.value = false;
  useAlert('Evento excluído');
}
async function copyScript(script) {
  await navigator.clipboard.writeText(script.body);
  useAlert(`"${script.title}" copiado pra área de transferência`);
}

async function refreshField() {
  await Promise.all([
    store.dispatch('electoralCampaign/fetchWorkers'),
    store.dispatch('electoralCampaign/fetchFieldSummary'),
    store.dispatch('electoralCampaign/fetchFieldByCity'),
  ]);
}
function openNewWorker() {
  editingWorker.value = null;
  workerModalOpen.value = true;
}
function openEditWorker(w) {
  editingWorker.value = w;
  workerModalOpen.value = true;
}
async function saveWorker(payload) {
  saving.value = true;
  try {
    if (payload.id) {
      await store.dispatch('electoralCampaign/updateWorker', payload);
      useAlert('Cabo atualizado');
    } else {
      await store.dispatch('electoralCampaign/createWorker', payload);
      useAlert('Cabo cadastrado');
    }
    workerModalOpen.value = false;
    await refreshField();
  } catch (e) {
    useAlert(e?.response?.data?.errors?.join(', ') || 'Erro ao salvar');
  } finally {
    saving.value = false;
  }
}
async function deleteWorker(w) {
  if (!window.confirm(`Excluir "${w.name}"?`)) return;
  await store.dispatch('electoralCampaign/deleteWorker', w.id);
  workerModalOpen.value = false;
  await refreshField();
  useAlert('Cabo excluído');
}
async function importWorkers(file) {
  try {
    const res = await store.dispatch('electoralCampaign/importWorkers', file);
    useAlert(`${res.inserted} cabos importados`);
    importModalOpen.value = false;
    await refreshField();
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Erro no import');
  }
}

function openNewAsset() {
  editingAsset.value = null;
  mediaModalOpen.value = true;
}
function openEditAsset(a) {
  editingAsset.value = a;
  mediaModalOpen.value = true;
}
async function saveAsset(payload) {
  saving.value = true;
  try {
    if (payload.id) {
      await store.dispatch('electoralCampaign/updateMediaAsset', payload);
      useAlert('Asset atualizado');
    } else {
      await store.dispatch('electoralCampaign/createMediaAsset', payload);
      useAlert('Asset criado');
    }
    mediaModalOpen.value = false;
  } catch (e) {
    useAlert(e?.response?.data?.errors?.join(', ') || 'Erro ao salvar');
  } finally {
    saving.value = false;
  }
}
async function deleteAsset(a) {
  if (!window.confirm(`Excluir "${a.title}"?`)) return;
  await store.dispatch('electoralCampaign/deleteMediaAsset', a.id);
  mediaModalOpen.value = false;
  useAlert('Asset excluído');
}

onMounted(async () => {
  await Promise.all([
    store.dispatch('electoralCampaign/fetchCampaign'),
    loadTasks(),
    store.dispatch('electoralCampaign/fetchEvents'),
    store.dispatch('electoralCampaign/fetchScripts'),
    refreshField(),
    store.dispatch('electoralCampaign/fetchMediaAssets'),
    loadMapAndPins(),
  ]);
  if (map.value?.status && ['pending', 'generating'].includes(map.value.status)) {
    startMapPolling();
  }
});

onBeforeUnmount(() => stopMapPolling());
</script>

<template>
  <div class="flex h-full w-full flex-col overflow-y-auto bg-n-background-subtle">
    <!-- Header -->
    <div class="border-b border-n-weak bg-n-background px-5 py-4">
      <div class="flex items-center justify-between gap-3">
        <div class="flex items-center gap-3">
          <div class="flex size-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-600 to-indigo-700 text-white">
            <span class="i-lucide-vote size-5" />
          </div>
          <div>
            <h1 class="text-lg font-bold text-n-slate-12">Campanha</h1>
            <p class="text-xs text-n-slate-11">
              Planejamento estratégico eleitoral
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabs -->
    <div class="border-b border-n-weak/60 bg-n-background px-5">
      <nav class="-mb-px flex gap-1">
        <button
          v-for="v in VIEWS"
          :key="v.value"
          type="button"
          :class="[
            'inline-flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-xs font-semibold transition',
            activeView === v.value
              ? 'border-blue-600 text-blue-700'
              : 'border-transparent text-n-slate-11 hover:text-n-slate-12',
          ]"
          @click="activeView = v.value"
        >
          <span :class="v.icon" class="size-3.5" />
          {{ v.label }}
        </button>
      </nav>
    </div>

    <!-- Content -->
    <div class="flex-1 p-5">
      <div v-if="uiFlags.isFetchingCampaign && !campaign" class="flex justify-center py-20">
        <Spinner />
      </div>

      <div v-else-if="activeView === 'overview'">
        <OverviewCards :campaign="campaign" />
      </div>

      <div v-else-if="activeView === 'tasks'" class="space-y-4">
        <div class="flex items-center justify-between gap-2">
          <FilterSelect
            v-model="categoryFilter"
            :options="categoryOptions"
            icon="i-lucide-layers"
            placeholder="Categoria"
          />
          <Button variant="solid" color="blue" size="sm" icon="i-lucide-plus" @click="openCreate">
            Nova tarefa
          </Button>
        </div>

        <div v-if="uiFlags.isFetchingTasks" class="flex justify-center py-10">
          <Spinner />
        </div>

        <TaskKanban v-else :tasks="filteredTasks" @edit="openEdit" />
      </div>

      <div v-else-if="activeView === 'calendar'" class="space-y-4">
        <div class="flex items-center justify-between gap-2">
          <div class="text-xs text-n-slate-11">
            {{ events.length }} evento(s) · clique num dia para criar
          </div>
          <Button variant="solid" color="blue" size="sm" icon="i-lucide-plus" @click="openNewEvent()">
            Novo evento
          </Button>
        </div>
        <EventCalendar :events="events" @edit="openEditEvent" @new-at="openNewEvent" />
      </div>

      <div v-else-if="activeView === 'scripts'" class="space-y-4">
        <div class="flex items-center justify-between gap-2">
          <FilterSelect
            v-model="scriptTypeFilter"
            :options="eventTypeOptions"
            icon="i-lucide-scroll-text"
            placeholder="Tipo de evento"
          />
          <span class="text-xs text-n-slate-11">{{ filteredScripts.length }} scripts</span>
        </div>
        <div class="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <ScriptCard
            v-for="s in filteredScripts"
            :key="s.id"
            :script="s"
            @use="copyScript"
          />
        </div>
        <div v-if="!filteredScripts.length" class="rounded-lg border border-dashed border-n-weak p-10 text-center text-sm text-n-slate-11">
          Nenhum script para este filtro.
        </div>
      </div>

      <div v-else-if="activeView === 'field'" class="space-y-4">
        <CountdownPanel :election-date="campaign?.electionDate" />
        <FieldKPIs :summary="fieldSummary" />
        <CityRanking :rows="fieldByCity" />

        <div class="flex flex-wrap items-center gap-2">
          <FilterSelect
            v-model="workerRoleFilter"
            :options="workerRoleOptions"
            icon="i-lucide-user-cog"
            placeholder="Papel"
          />
          <FilterSelect
            v-model="workerCityFilter"
            :options="workerCityOptions"
            icon="i-lucide-map-pin"
            placeholder="Cidade"
          />
          <span class="text-xs text-n-slate-11">{{ filteredWorkers.length }} de {{ workers.length }}</span>
          <div class="ml-auto flex gap-2">
            <Button variant="outline" size="sm" icon="i-lucide-upload" @click="importModalOpen = true">
              Importar CSV
            </Button>
            <Button variant="solid" color="blue" size="sm" icon="i-lucide-plus" @click="openNewWorker">
              Novo cabo
            </Button>
          </div>
        </div>

        <WorkerTable :workers="filteredWorkers" @edit="openEditWorker" />
      </div>

      <div v-else-if="activeView === 'media'" class="space-y-4">
        <div class="flex flex-wrap items-center gap-2">
          <FilterSelect
            v-model="mediaTypeFilter"
            :options="mediaTypeOptions"
            icon="i-lucide-layers"
            placeholder="Tipo"
          />
          <FilterSelect
            v-model="mediaPlatformFilter"
            :options="mediaPlatformOptions"
            icon="i-lucide-globe"
            placeholder="Plataforma"
          />
          <span class="text-xs text-n-slate-11">{{ filteredAssets.length }} assets</span>
          <Button class="ml-auto" variant="solid" color="blue" size="sm" icon="i-lucide-plus" @click="openNewAsset">
            Novo asset
          </Button>
        </div>
        <MediaKanban :assets="filteredAssets" @edit="openEditAsset" />
      </div>

      <div v-else-if="activeView === 'map'" class="space-y-4">
        <MapGenerator
          v-if="!map || map.status === 'failed'"
          :generating="mapGenerating"
          @generate="generateMap"
        />

        <div
          v-if="map && (map.status === 'pending' || map.status === 'generating')"
          class="rounded-xl border border-dashed border-blue-400 bg-blue-50 p-8 text-center"
        >
          <div class="mx-auto mb-2 flex size-10 items-center justify-center rounded-full bg-blue-600 text-white">
            <span class="i-lucide-loader-2 size-5 animate-spin" />
          </div>
          <p class="text-sm font-semibold text-blue-900">
            Gerando mapa de {{ map.city }}/{{ map.state }}…
          </p>
          <p class="text-xs text-blue-700">
            Buscando contorno no IBGE e bairros no OpenStreetMap. Pode demorar alguns segundos.
          </p>
        </div>

        <div v-if="map?.status === 'failed'" class="flex items-center justify-between gap-3 rounded-xl border border-rose-300 bg-rose-50 p-4 text-sm text-rose-800">
          <div>
            <strong>Falha:</strong> {{ map.errorMessage || 'Erro ao gerar o mapa.' }}
          </div>
          <Button variant="outline" size="sm" icon="i-lucide-trash-2" color="ruby" @click="askDeleteMap">
            Excluir
          </Button>
        </div>

        <div v-if="map?.status === 'ready'" class="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_280px]">
          <MapCanvas
            :map="map"
            :pins="pins"
            :active-types="activePinTypes"
            @click-coord="onMapClick"
            @edit-pin="onPinClick"
          />

          <div class="space-y-3">
            <div class="rounded-xl border border-n-strong/40 bg-n-background p-4">
              <p class="mb-2 text-xs font-semibold text-n-slate-11">Município</p>
              <p class="text-base font-bold text-n-slate-12">{{ map.city }} / {{ map.state }}</p>
              <p class="text-[11px] text-n-slate-11">
                IBGE {{ map.ibgeCode }} · {{ map.neighborhoods?.length || 0 }} bairros
              </p>
              <Button
                class="mt-3 w-full"
                variant="outline"
                size="sm"
                icon="i-lucide-trash-2"
                color="ruby"
                @click="askDeleteMap"
              >
                Excluir mapa
              </Button>
            </div>

            <div class="rounded-xl border border-n-strong/40 bg-n-background p-4">
              <p class="mb-2 text-xs font-semibold text-n-slate-11">Camadas de pin</p>
              <div class="space-y-1.5">
                <button
                  v-for="t in PIN_TYPES"
                  :key="t.value"
                  type="button"
                  :class="[
                    'flex w-full items-center justify-between rounded-md border px-2 py-1.5 text-xs font-medium transition',
                    activePinTypes.includes(t.value)
                      ? `${t.bg} ${t.text} border-transparent`
                      : 'border-n-weak bg-n-slate-2 text-n-slate-11',
                  ]"
                  @click="togglePinType(t.value)"
                >
                  <span class="flex items-center gap-1.5">
                    <span :class="[t.color, 'flex size-4 items-center justify-center rounded-full']">
                      <span :class="t.icon" class="size-2.5 text-white" />
                    </span>
                    {{ t.label }}
                  </span>
                  <span class="text-[10px]">{{ pinCounts[t.value] || 0 }}</span>
                </button>
              </div>
            </div>

            <div class="rounded-xl border border-n-strong/40 bg-n-background p-3 text-[11px] text-n-slate-11">
              <p class="font-semibold text-n-slate-12">Dica</p>
              <p class="mt-1">Clique em qualquer ponto do mapa para criar um pin ali. Cabos cadastrados com lat/lng aparecem automaticamente.</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <PinModal
      :open="pinModalOpen"
      :pin="editingPin"
      :coord="pendingCoord"
      :saving="saving"
      @close="pinModalOpen = false"
      @save="savePin"
      @delete="deletePin"
    />

    <DeleteModal
      v-model:show="deleteMapOpen"
      :on-close="() => (deleteMapOpen = false)"
      :on-confirm="confirmDeleteMap"
      title="Excluir mapa"
      :message="
        map
          ? `Tem certeza que deseja excluir o mapa de ${map.city}/${map.state}? Todos os pins manuais criados sobre ele serão apagados. Os cabos cadastrados continuam salvos — só a geometria do mapa será removida. Você poderá gerar um novo mapa em seguida.`
          : ''
      "
      confirm-text="Excluir mapa"
      reject-text="Cancelar"
    />

    <MediaModal
      :open="mediaModalOpen"
      :asset="editingAsset"
      :saving="saving"
      @close="mediaModalOpen = false"
      @save="saveAsset"
      @delete="deleteAsset"
    />

    <WorkerModal
      :open="workerModalOpen"
      :worker="editingWorker"
      :leaders="leaderWorkers"
      :saving="saving"
      @close="workerModalOpen = false"
      @save="saveWorker"
      @delete="deleteWorker"
    />

    <WorkerImportModal
      :open="importModalOpen"
      @close="importModalOpen = false"
      @import="importWorkers"
    />

    <EventModal
      :open="eventModalOpen"
      :event="editingEvent"
      :scripts="scripts"
      :saving="saving"
      :default-date="eventDefaultDate"
      @close="eventModalOpen = false"
      @save="saveEvent"
      @delete="deleteEvent"
    />

    <TaskModal
      :open="taskModalOpen"
      :task="editingTask"
      :saving="saving"
      @close="taskModalOpen = false"
      @save="saveTask"
      @delete="deleteTask"
    />
  </div>
</template>
